"use client";

/**
 * Styled replacement for window.confirm() - a promise-based hook so
 * call sites keep their linear `if (!(await confirmDlg(...))) return;`
 * shape instead of splitting into open/confirm handler pairs.
 *
 *   const [confirmDlg, confirmEl] = useConfirm();
 *   ...
 *   if (!(await confirmDlg({ body: t("..."), danger: true }))) return;
 *   ...
 *   return <>{...page...}{confirmEl}</>;
 *
 * Portaled to body (ancestors with transforms/backdrop-filter can't
 * re-anchor it) with the app's modal enter animations. Escape and
 * backdrop-click resolve to false, mirroring native confirm's cancel.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useT } from "@/lib/locale-context";
import BottomSheet from "@/components/BottomSheet";
import { useIsMobileViewport } from "@/lib/use-install-prompt";

export interface ConfirmOptions {
  /** Question body - the one required piece. */
  body: string;
  /** Bold headline above the body; defaults to a generic "Are you sure?". */
  title?: string;
  /** Quiet continue-action copy (resolves TRUE); default t("confirm.ok"). */
  confirmLabel?: string;
  /** Primary go-back copy (resolves FALSE); default t("confirm.cancel"). */
  cancelLabel?: string;
  /** Destructive continue: its quiet button tints rose on hover. */
  danger?: boolean;
}

interface Pending {
  opts: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

export function useConfirm(): [
  (opts: ConfirmOptions) => Promise<boolean>,
  ReactNode,
] {
  const t = useT();
  const [pending, setPending] = useState<Pending | null>(null);
  // Phones get the shared bottom sheet, desktop the centred card (owner,
  // 2026-10-12: "a pop-up modal on desktop and a sheet on mobile").
  const mobile = useIsMobileViewport();

  const ask = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => setPending({ opts, resolve })),
    [],
  );

  const settle = useCallback(
    (ok: boolean) => {
      setPending((p) => {
        p?.resolve(ok);
        return null;
      });
    },
    [],
  );

  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") settle(false);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [pending, settle]);

  const buttons = pending && (
    <div className="space-y-2">
      <button
        type="button"
        // Enter is the safe path on desktop; on a phone the autofocus only
        // painted a focus ring on the sheet's button.
        autoFocus={!mobile}
        onClick={() => settle(false)}
        className="w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700"
      >
        {pending.opts.cancelLabel || t("confirm.cancel")}
      </button>
      <button
        type="button"
        onClick={() => settle(true)}
        className={`w-full rounded-xl py-2.5 text-sm font-semibold text-gray-500 transition-colors ${
          pending.opts.danger
            ? "hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10 dark:hover:text-rose-400"
            : "hover:bg-gray-50 hover:text-gray-700"
        }`}
      >
        {pending.opts.confirmLabel || t("confirm.ok")}
      </button>
    </div>
  );

  const el = pending && mobile
    ? (
      <BottomSheet open onClose={() => settle(false)} title={pending.opts.title || t("confirm.title")} footer={buttons}>
        <p className="pt-1 text-sm leading-relaxed text-gray-600">{pending.opts.body}</p>
      </BottomSheet>
    )
    : pending && typeof document !== "undefined"
      ? createPortal(
          <div
            role="alertdialog"
            aria-modal="true"
            onClick={() => settle(false)}
            className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4 modal-backdrop-enter"
          >
            {/* Retention-first emphasis (owner call): the PRIMARY indigo
                action is "go back" (autofocused - Enter is the safe
                path); continuing the destructive thing is a quiet text
                action underneath. No icon tile, no filled danger slab -
                border + soft shadow per DESIGN.md surface rules. */}
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-[0_24px_64px_-24px_rgba(15,23,42,0.35)] modal-enter"
            >
              <p className="text-base font-bold tracking-tight text-gray-900">
                {pending.opts.title || t("confirm.title")}
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                {pending.opts.body}
              </p>
              <div className="mt-5">{buttons}</div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return [ask, el];
}
